/**
 * KioskAttractorView.tsx
 *
 * Eye-catching promotional attractor loop for self-service kiosks.
 * Displays vibrant visuals, featured specials, and large touch CTA.
 */

import React from 'react';

export interface KioskAttractorViewProps {
  readonly storeName: string;
  readonly onStartShopping: () => void;
  readonly onBrandLogoTap?: () => void;
}

export const KioskAttractorView: React.FC<KioskAttractorViewProps> = ({
  storeName,
  onStartShopping,
  onBrandLogoTap,
}) => {
  return (
    <div
      onClick={onStartShopping}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#090d16',
        backgroundImage: 'radial-gradient(circle at 50% 40%, #1e1b4b 0%, #030712 100%)',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 40,
        cursor: 'pointer',
        userSelect: 'none',
        zIndex: 1000,
      }}
    >
      {/* Brand Header with 5-tap escape target */}
      <div
        onClick={(e) => {
          e.stopPropagation();
          onBrandLogoTap?.();
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 24px',
          backgroundColor: 'rgba(255,255,255,0.05)',
          borderRadius: 30,
          backdropFilter: 'blur(10px)',
        }}
      >
        <span style={{ fontSize: '2rem' }}>🌿</span>
        <h1 style={{ margin: 0, fontSize: '1.8rem', fontWeight: 800, letterSpacing: '0.05em' }}>
          {storeName}
        </h1>
      </div>

      {/* Hero Content */}
      <div style={{ textAlign: 'center', maxWidth: 650 }}>
        <div
          style={{
            display: 'inline-block',
            padding: '6px 16px',
            backgroundColor: '#3b82f6',
            color: '#fff',
            borderRadius: 20,
            fontSize: '0.9rem',
            fontWeight: 'bold',
            marginBottom: 20,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
          }}
        >
          Fresh • Local • Sovereign
        </div>
        <h2
          style={{
            fontSize: '3.2rem',
            lineHeight: 1.15,
            fontWeight: 900,
            margin: '0 0 20px 0',
            background: 'linear-gradient(135deg, #ffffff 0%, #94a3b8 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Order & Pay in Seconds
        </h2>
        <p style={{ fontSize: '1.2rem', color: '#94a3b8', margin: 0 }}>
          Browse our seasonal menu, customize your order, and pick up fresh at the counter.
        </p>
      </div>

      {/* Large Touch CTA Button */}
      <div style={{ textAlign: 'center', marginBottom: 30 }}>
        <button
          type="button"
          onClick={onStartShopping}
          style={{
            padding: '22px 64px',
            fontSize: '1.6rem',
            fontWeight: 900,
            borderRadius: 50,
            border: 'none',
            backgroundColor: '#10b981',
            color: '#fff',
            cursor: 'pointer',
            boxShadow: '0 20px 35px -10px rgba(16, 185, 129, 0.6)',
            transition: 'transform 0.2s',
          }}
        >
          👉 Touch Anywhere to Begin
        </button>
        <div style={{ marginTop: 16, fontSize: '0.95rem', color: '#64748b' }}>
          Tap screen to explore menu
        </div>
      </div>
    </div>
  );
};
